import React, { FunctionComponent, useContext, useMemo } from "react";
import {
    ContentSection,
    GridRow,
    GridColumn,
    ListTable,
    ButtonRenderProps,
    ButtonProps,
    ActionButton,
    Checkbox,
    DetailsRowProps,
    Grid,
    Stack,
    RenderFunction,
    DropdownMultiselect,
    depascalize
} from "sprint-common-web";

import {
    CreateContentVersionButton,
    CreateVersionButtonProps
} from "../../../../../common/components/content-version/CreateContentVersionButton";
import { TagElementsList } from "../../../../../common/components/TagElementsList";
import { ContentReferenceType } from "../../../../../common/types/ContentReferenceType";
import { ContentVersionSource } from "../../../../common/types/ContentVersionSource";
import { getSlicedText } from "../../../../common/utils/getSlicedText";
import { renderNotMergableRow } from "../../../../common/utils/renderNotMergableRow";
import { validateVersionsMergeability } from "../../../../common/utils/validateVersionsMergeability";
import { EpisodeVersionDetails } from "../../../TvSeriesDetails";
import { MergeEpisodeVersionsCommandBarButton } from "./buttons/MergeEpisodeVersionsCommandBarButton";
import { EpisodeVersionListContext } from "./context/EpisodeVersionListProvider";
import { createEpisodeVersionListingColumns } from "./createEpisodeVersionListingColumns";
import { episodeVersionOrigins } from "./EpisodeVersionListingFilters";
import { EpisodeVersionListingItem } from "./EpisodeVersionListingItem";

export const EpisodeVersionListing: FunctionComponent = () => {
    const {
        listingItems,
        versionsToMerge,
        filters,
        totalItems,
        selectedEpisode,
        selectedTvSeries,
        setSort,
        setPage,
        setPageSize,
        setSources,
        setIncludeMerged,
        clearFilters,
        removeVersionFromMergeList,
        addEpisodeVersion
    } = useContext(EpisodeVersionListContext);
    const columns = useMemo(createEpisodeVersionListingColumns, []);

    const handleMergedCheckboxChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
        checked?: boolean | undefined
    ) => {
        setIncludeMerged(checked);
    };

    const renderRow = (rowProps?: DetailsRowProps, renderDefault?: RenderFunction<DetailsRowProps>) => {
        if (isRowMergable(selectedEpisode.versions, versionsToMerge[0], rowProps?.item) && renderDefault) {
            return renderDefault(rowProps, renderDefault);
        }

        return renderNotMergableRow(rowProps, renderDefault);
    };

    const sourceOptions = episodeVersionOrigins.map((source) => ({
        key: source,
        text: depascalize(ContentVersionSource[source])
    }));

    const createNewVersionButtonProps: ButtonRenderProps<CreateVersionButtonProps> = {
        render: CreateContentVersionButton,
        content: {
            type: ContentReferenceType.TvSeries,
            tvSeriesId: selectedTvSeries.id,
            episodeId: selectedEpisode.id
        },
        onContentVersionCreated: (newVersion) => {
            addEpisodeVersion(newVersion);
        },
        isLabeled: true
    };

    const mergeVersionsButtonProps: ButtonRenderProps<ButtonProps> = {
        render: MergeEpisodeVersionsCommandBarButton
    };

    return (
        <>
            <ContentSection header="Versions" buttons={[mergeVersionsButtonProps, createNewVersionButtonProps]}>
                <Grid>
                    <GridRow style={{ margin: 0 }}>
                        <TagElementsList
                            items={versionsToMerge}
                            onItemDismiss={removeVersionFromMergeList}
                            renderItemText={getMergeEpisodeVersionText}
                            styles={{ root: { paddingBottom: 10 } }}
                        />
                    </GridRow>
                    <GridRow style={{ margin: 0 }}>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Version Source"
                                selectedKeys={filters.sources}
                                onChange={setSources}
                                options={sourceOptions}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <Stack styles={{ root: { paddingTop: 5 } }}>
                                <Checkbox
                                    label="Include Merge Sources"
                                    onChange={handleMergedCheckboxChange}
                                    checked={filters.includeMerged ?? false}
                                />
                            </Stack>
                        </GridColumn>
                        <GridColumn sm={8}>
                            <Stack horizontalAlign="end">
                                <ActionButton
                                    iconProps={{ iconName: "ClearFilter" }}
                                    ariaLabel="Clear filters"
                                    label="Clear filters"
                                    onClick={clearFilters}
                                >
                                    Clear filters
                                </ActionButton>
                            </Stack>
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={12}>
                            <ListTable<EpisodeVersionListingItem>
                                columns={columns}
                                items={listingItems}
                                pagination={{
                                    ...filters,
                                    totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                onSortChange={setSort}
                                isLoading={false}
                                onRenderRow={versionsToMerge.length ? renderRow : undefined}
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};

export const getMergeEpisodeVersionText = (item: EpisodeVersionListingItem, sliceBuzzVersion = true): string => {
    return `${item.distributorName} - ${item.typeName}${item.brandings.length ? ` - ${item.brandings}` : ""}${
        !!item.buzzVersion?.name
            ? ` - ${sliceBuzzVersion ? getSlicedText(item.buzzVersion.name, 0, 40, "...") : item.buzzVersion.name}`
            : ""
    }`;
};

const isRowMergable = (
    versionsDetails: EpisodeVersionDetails[],
    versionToMerge?: EpisodeVersionListingItem,
    rowItem?: EpisodeVersionListingItem
) => {
    const versionsDetailsToMerge =
        versionsDetails.filter(
            (version: EpisodeVersionDetails) => version.id === versionToMerge?.id || version.id === rowItem?.id
        ) ?? [];

    const areMergeable = validateVersionsMergeability(versionsDetailsToMerge);

    return areMergeable || versionToMerge?.id === rowItem?.id;
};
